import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waContactService,
  WaContactFilters,
  CreateWaContactRequest,
  UpdateWaContactRequest,
  BulkWaContactRequest,
} from '@services/wa-contact.service'

export function useWaContacts(filters: WaContactFilters = {}) {
  return useQuery({
    queryKey: ['waContacts', filters],
    queryFn: () => waContactService.fetchContacts(filters),
  })
}

export function useWaContact(id: number) {
  return useQuery({
    queryKey: ['waContact', id],
    queryFn: () => waContactService.getContact(id),
    enabled: !!id,
  })
}

export function useWaTags() {
  return useQuery({
    queryKey: ['waTags'],
    queryFn: waContactService.fetchTags,
    staleTime: 300000, // Cache for 5 minutes
  })
}

export function useCreateWaContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateWaContactRequest) => waContactService.createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waContacts'] })
    },
  })
}

export function useUpdateWaContact(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateWaContactRequest) => waContactService.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waContacts'] })
      queryClient.invalidateQueries({ queryKey: ['waContact', id] })
    },
  })
}

export function useDeleteWaContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waContactService.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waContacts'] })
    },
  })
}

export function useBulkWaContactAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BulkWaContactRequest) => waContactService.bulkAction(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waContacts'] })
    },
  })
}

export function useBlockWaContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waContactService.blockContact(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waContacts'] })
      queryClient.invalidateQueries({ queryKey: ['waContact', id] })
    },
  })
}

export function useUnblockWaContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waContactService.unblockContact(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waContacts'] })
      queryClient.invalidateQueries({ queryKey: ['waContact', id] })
    },
  })
}

// Tag mutations
export function useCreateWaTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { name: string; color?: string; description?: string }) =>
      waContactService.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waTags'] })
    },
  })
}

export function useUpdateWaTag(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { name?: string; color?: string; description?: string }) =>
      waContactService.updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waTags'] })
    },
  })
}

export function useDeleteWaTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waContactService.deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waTags'] })
      queryClient.invalidateQueries({ queryKey: ['waContacts'] })
    },
  })
}
